import { cn } from "../lib/cn";
import { Button } from "./button";
import { Icon } from "./icon";

export type PageToken = number | "ellipsis-start" | "ellipsis-end";

/** 1-based page list with ellipses, always including first, last and `siblings` around current. */
export function paginationRange(page: number, pageCount: number, siblings = 1): PageToken[] {
  if (pageCount <= 0) return [];
  const total = siblings * 2 + 5;
  if (pageCount <= total) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const start = Math.max(page - siblings, 2);
  const end = Math.min(page + siblings, pageCount - 1);
  const tokens: PageToken[] = [1];
  if (start > 2) tokens.push("ellipsis-start");
  for (let p = start; p <= end; p++) tokens.push(p);
  if (end < pageCount - 1) tokens.push("ellipsis-end");
  tokens.push(pageCount);
  return tokens;
}

export interface PaginationLabels {
  nav: string;
  previous: string;
  next: string;
  page: (n: number) => string;
  summary?: (page: number, pageCount: number) => string;
}

const DEFAULT_LABELS: PaginationLabels = {
  nav: "Pagination",
  previous: "Previous page",
  next: "Next page",
  page: (n) => `Page ${n}`,
  summary: (page, count) => `Page ${page} of ${count}`,
};

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  labels?: Partial<PaginationLabels>;
  className?: string;
}

export function Pagination({ page, pageCount, onPageChange, labels, className }: PaginationProps) {
  const l = { ...DEFAULT_LABELS, ...labels };
  if (pageCount <= 1) return null;
  return (
    <nav aria-label={l.nav} className={cn("flex items-center justify-between gap-3", className)}>
      {l.summary ? <p className="text-xs text-fg-subtle tabular-nums">{l.summary(page, pageCount)}</p> : <span />}
      <ul className="flex items-center gap-1">
        <li>
          <Button variant="ghost" size="sm" iconOnly aria-label={l.previous} disabled={page <= 1} onClick={() => { onPageChange(page - 1); }}>
            <Icon name="chevron-left" />
          </Button>
        </li>
        {paginationRange(page, pageCount).map((token) =>
          typeof token === "number" ? (
            <li key={token}>
              <Button
                variant={token === page ? "secondary" : "ghost"}
                size="sm"
                aria-label={l.page(token)}
                aria-current={token === page ? "page" : undefined}
                className="min-w-8 tabular-nums"
                onClick={() => {
                  onPageChange(token);
                }}
              >
                {token}
              </Button>
            </li>
          ) : (
            <li key={token} aria-hidden="true" className="px-1 text-xs text-fg-subtle">
              …
            </li>
          ),
        )}
        <li>
          <Button variant="ghost" size="sm" iconOnly aria-label={l.next} disabled={page >= pageCount} onClick={() => { onPageChange(page + 1); }}>
            <Icon name="chevron-right" />
          </Button>
        </li>
      </ul>
    </nav>
  );
}
